import React, { useState } from "react";
import { uploadApi } from "../../../services/api/uploadApi";
import { postApi } from "../../../services/api/postApi";
import { FaFilePdf, FaTimes } from "react-icons/fa";
import "./PostForms.css";

const ResearchPostForm = ({ onSuccess, onCancel }) => {
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [authors, setAuthors] = useState("");
  const [abstract, setAbstract] = useState("");
  const [publicationYear, setPublicationYear] = useState("");
  const [journal, setJournal] = useState("");
  const [doi, setDoi] = useState("");
  const [keywords, setKeywords] = useState("");
  const [field, setField] = useState("");
  const [subfield, setSubfield] = useState("");
  const [tags, setTags] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfKey, setPdfKey] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [posting, setPosting] = useState(false);

  // Handle PDF selection
  const handlePdfSelect = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    // Validate file type
    if (file.type !== "application/pdf") {
      alert("Only PDF files are allowed");
      return;
    }

    // Validate file size (max 25MB)
    if (file.size > 25 * 1024 * 1024) {
      alert("PDF file must be less than 25MB");
      return;
    }

    setPdfFile(file);
    setUploading(true);

    try {
      const result = await uploadApi.uploadPaperPdf(file, (progress) => {
        setUploadProgress(progress);
      });

      setPdfKey(result.key);
      alert("PDF uploaded successfully!");
    } catch (error) {
      alert("PDF upload failed");
      setPdfFile(null);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Remove PDF
  const removePdf = () => {
    setPdfFile(null);
    setPdfKey(null);
  };

  // Submit post
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      alert("Please enter a paper title");
      return;
    }

    if (!abstract.trim()) {
      alert("Please enter an abstract");
      return;
    }

    if (!authors.trim()) {
      alert("Please enter at least one author");
      return;
    }

    setPosting(true);
    try {
      const response = await postApi.createPost({
        postType: "research",
        content: content.trim(),
        researchPaper: {
          title: title.trim(),
          abstract: abstract.trim(),
          authors: authors
            .split(",")
            .map((a) => a.trim())
            .filter(Boolean),
          publicationYear: publicationYear ? parseInt(publicationYear) : null,
          journal: journal.trim() || undefined,
          pdfKey: pdfKey || undefined,
          doi: doi.trim() || undefined,
          keywords: keywords
            .split(",")
            .map((k) => k.trim())
            .filter(Boolean),
          field: field.trim() || undefined,
          subfield: subfield.trim() || undefined,
        },
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        visibility: visibility,
      });

      onSuccess(response.data.post);
    } catch (error) {
      alert("Failed to create post");
    } finally {
      setPosting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="post-form research-post-form">
      <div className="form-group">
        <label htmlFor="title" className="required">
          Paper Title
        </label>
        <input
          type="text"
          id="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter the title of your research paper"
          maxLength={500}
          required
        />
        <div className="char-count">{title.length}/500</div>
      </div>

      <div className="form-group">
        <label htmlFor="abstract" className="required">
          Abstract
        </label>
        <textarea
          id="abstract"
          value={abstract}
          onChange={(e) => setAbstract(e.target.value)}
          placeholder="Enter the abstract or summary of your paper..."
          rows={5}
          maxLength={5000}
          required
        />
        <div className="char-count">{abstract.length}/5000</div>
      </div>

      <div className="form-group">
        <label htmlFor="authors" className="required">
          Authors
        </label>
        <input
          type="text"
          id="authors"
          value={authors}
          onChange={(e) => setAuthors(e.target.value)}
          placeholder="Smith J, Doe A, Lee K (comma-separated)"
          required
        />
        <small className="form-hint">
          Enter author names separated by commas
        </small>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="publicationYear">Publication Year</label>
          <input
            type="number"
            id="publicationYear"
            value={publicationYear}
            onChange={(e) => setPublicationYear(e.target.value)}
            placeholder="2024"
            min="1900"
            max={new Date().getFullYear() + 1}
          />
        </div>

        <div className="form-group">
          <label htmlFor="journal">Journal/Venue</label>
          <input
            type="text"
            id="journal"
            value={journal}
            onChange={(e) => setJournal(e.target.value)}
            placeholder="e.g., Nature, IEEE"
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="doi">DOI</label>
        <input
          type="text"
          id="doi"
          value={doi}
          onChange={(e) => setDoi(e.target.value)}
          placeholder="10.1038/s41586-..."
        />
      </div>

      <div className="form-group">
        <label htmlFor="keywords">Keywords</label>
        <input
          type="text"
          id="keywords"
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          placeholder="ML, AI, Deep Learning (comma-separated)"
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="field">Field</label>
          <select
            id="field"
            value={field}
            onChange={(e) => setField(e.target.value)}
          >
            <option value="">Select a field...</option>
            <option value="Computer Science">Computer Science</option>
            <option value="Biology">Biology</option>
            <option value="Chemistry">Chemistry</option>
            <option value="Physics">Physics</option>
            <option value="Mathematics">Mathematics</option>
            <option value="Medicine">Medicine</option>
            <option value="Engineering">Engineering</option>
            <option value="Psychology">Psychology</option>
            <option value="Economics">Economics</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="subfield">Subfield</label>
          <input
            type="text"
            id="subfield"
            value={subfield}
            onChange={(e) => setSubfield(e.target.value)}
            placeholder="e.g., Machine Learning"
          />
        </div>
      </div>

      {/* PDF Upload */}
      <div className="pdf-upload-section">
        <label>PDF File (optional)</label>
        {!pdfFile ? (
          <>
            <input
              type="file"
              accept="application/pdf"
              onChange={handlePdfSelect}
              disabled={uploading}
              id="pdf-input"
              style={{ display: "none" }}
            />
            <label
              htmlFor="pdf-input"
              className={`upload-label ${uploading ? "disabled" : ""}`}
            >
              <FaFilePdf />
              {uploading ? `Uploading... ${uploadProgress}%` : "Upload PDF"}
            </label>
          </>
        ) : (
          <div className="pdf-preview">
            <FaFilePdf className="pdf-icon" />
            <span className="pdf-name">{pdfFile.name}</span>
            <button
              type="button"
              onClick={removePdf}
              className="remove-pdf-btn"
            >
              <FaTimes />
            </button>
          </div>
        )}
        {uploading && (
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="content">Personal Commentary (optional)</label>
        <textarea
          id="content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Share your thoughts about this paper..."
          rows={3}
          maxLength={5000}
        />
        <div className="char-count">{content.length}/5000</div>
      </div>

      <div className="form-group">
        <label htmlFor="tags">Tags (optional)</label>
        <input
          type="text"
          id="tags"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="AI, research, collaboration (comma-separated)"
        />
      </div>

      <div className="form-group">
        <label htmlFor="visibility">Visibility</label>
        <select
          id="visibility"
          value={visibility}
          onChange={(e) => setVisibility(e.target.value)}
        >
          <option value="public">Public</option>
          <option value="connections">Connections Only</option>
          <option value="private">Private</option>
        </select>
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="cancel-btn">
          Cancel
        </button>
        <button
          type="submit"
          disabled={
            uploading ||
            posting ||
            !title.trim() ||
            !abstract.trim() ||
            !authors.trim()
          }
          className="submit-btn"
        >
          {posting ? "Posting..." : "Post"}
        </button>
      </div>
    </form>
  );
};

export default ResearchPostForm;
